import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Button,
  Stack,
  Fade,
  Paper,
  InputAdornment
} from '@mui/material';
import {
  Send,
  Mic,
  MicOff,
  Phone,
  SmartToy,
  Person,
  ArrowBack,
  MoreVert,
  History,
  Language,
  Psychology,
  ShieldMoon,
  Logout
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSocket } from '../contexts/SocketContext';
import { useVoice } from '../contexts/VoiceContext';
import { saveMessage, getMessages, addToSyncQueue, getSyncQueue, clearSyncQueue } from '../db';

const GanaderoMobile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sendGanaderoMessage, messages: socketMessages } = useSocket();
  const { isListening: isVoiceListening, startListening, stopListening, fullTranscript, resetTranscript, speak, changeLanguage, interimTranscript } = useVoice();
  const messagesEndRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  
  const [currentMessage, setCurrentMessage] = useState('');
  const [isCallActive, setIsCallActive] = useState(false);
  const [language, setLanguage] = useState('es');

  // Use unified socketMessages from context
  const messages = socketMessages.map(m => ({
    type: m.emisor === 'admin' ? 'assistant' : 'user',
    text: m.texto,
    timestamp: m.fecha
  }));

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    
    // Auto-TTS for Admin Messages
    if (socketMessages.length > lastMessageCountRef.current) {
      const msgs = socketMessages;
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg && lastMsg.emisor === 'admin') {
        speak(lastMsg.texto);
      }
      lastMessageCountRef.current = msgs.length;
    }
  }, [socketMessages, speak]);

  // Sync Voice Transcript to Local State
  useEffect(() => {
    if (fullTranscript) {
      setCurrentMessage(fullTranscript);
    }
  }, [fullTranscript]);

  const handleSendMessage = async (text = currentMessage) => {
    if (!text.trim()) return;
    
    // We rely on SocketContext polling for the update
    setCurrentMessage('');
    
    try {
      await fetch('/api/mensajes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emisor: 'ganadero', texto: text })
      });
    } catch (err) { console.error("Error sending message", err); }
  };

  const toggleCall = () => {
    setIsCallActive(!isCallActive);
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: '#F8FAFC',
      maxWidth: '500px',
      margin: '0 auto',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: '0 0 40px rgba(0,0,0,0.1)'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 2, 
        bgcolor: 'white', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid var(--border-color)',
        boxShadow: 'var(--shadow-sm)'
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton size="small" onClick={() => { localStorage.clear(); navigate('/'); }} sx={{ bgcolor: 'rgba(239, 68, 68, 0.1)', color: 'var(--alert-crimson)' }}>
            <Logout fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'var(--navy-main)', lineHeight: 1 }}>ADITU MOBILE</Typography>
            <Typography variant="caption" sx={{ color: 'var(--emerald-main)', fontWeight: 700 }}>VERIFICADO • V3.5</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Chip 
            label={language.toUpperCase()} 
            size="small" 
            onClick={() => {
              const newLang = language === 'es' ? 'eu' : 'es';
              setLanguage(newLang);
              changeLanguage(newLang);
            }}
            sx={{ fontWeight: 800, bgcolor: 'var(--slate-bg)', color: 'var(--navy-main)' }}
          />
          <IconButton onClick={toggleCall} sx={{ color: isCallActive ? 'var(--alert-crimson)' : 'var(--navy-main)' }}>
            <Phone />
          </IconButton>
        </Stack>
      </Box>

      {/* Chat Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {messages.map((m, i) => (
          <Fade in key={i}>
            <Box sx={{ display: 'flex', justifyContent: m.type === 'assistant' ? 'flex-start' : 'flex-end', gap: 1.5 }}>
              {m.type === 'assistant' && <Avatar sx={{ bgcolor: 'var(--navy-main)', width: 32, height: 32 }}><SmartToy sx={{ fontSize: 18 }} /></Avatar>}
              <Box sx={{ maxWidth: '80%' }}>
                <Paper sx={{ 
                  p: 2, 
                  borderRadius: m.type === 'assistant' ? '2px 20px 20px 20px' : '20px 2px 20px 20px',
                  bgcolor: m.type === 'assistant' ? 'white' : 'var(--navy-main)',
                  color: m.type === 'assistant' ? 'var(--navy-main)' : 'white',
                  boxShadow: 'var(--shadow-sm)',
                  border: m.type === 'assistant' ? '1px solid var(--border-color)' : 'none'
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>{m.text}</Typography>
                </Paper>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.5, textAlign: m.type === 'assistant' ? 'left' : 'right' }}>
                  {new Date(m.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
              </Box>
              {m.type === 'user' && <Avatar sx={{ bgcolor: 'var(--emerald-main)', width: 32, height: 32 }}><Person sx={{ fontSize: 18 }} /></Avatar>}
            </Box>
          </Fade>
        ))}
        <div ref={messagesEndRef} />
      </Box>

      {/* Quick Actions Sticky */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
          {['Alta Nacimiento', 'Guía Salida', 'Ver Censo', 'Alertas'].map((act, i) => (
            <Chip 
              key={i} 
              label={act} 
              clickable 
              onClick={() => handleSendMessage(act)}
              sx={{ fontWeight: 700, bgcolor: 'white', border: '1px solid var(--border-color)', fontSize: '11px' }} 
            />
          ))}
        </Stack>
      </Box>

      {/* Voice Status Indicator */}
      {isVoiceListening && (
        <Box sx={{ px: 2, mb: 1, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'var(--alert-crimson)', fontWeight: 700, animation: 'pulse 1s infinite' }}>
            {language === 'es' ? 'ESCUCHANDO (ES)...' : 'ENTZUTEN (EU)...'}
          </Typography>
          <Typography variant="body2" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
            {interimTranscript || 'Hable ahora...'}
          </Typography>
        </Box>
      )}

      {/* Input Area */}
      <Box sx={{ p: 2, bgcolor: 'white', borderTop: '1px solid var(--border-color)' }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <IconButton 
            onClick={() => {
              if (isVoiceListening) {
                stopListening();
                setTimeout(() => { if (currentMessage.trim()) handleSendMessage(); resetTranscript(); }, 500);
              } else {
                startListening();
              }
            }}
            sx={{ 
              bgcolor: isVoiceListening ? 'var(--alert-crimson)' : 'var(--slate-bg)', 
              color: isVoiceListening ? 'white' : 'var(--navy-main)',
              width: 48, height: 48,
              animation: isVoiceListening ? 'pulse 1.5s infinite' : 'none'
            }}
          >
            {isVoiceListening ? <MicOff /> : <Mic />}
          </IconButton>
          <TextField
            fullWidth
            placeholder="Mensaje o instrucción..."
            value={currentMessage}
            onChange={(e) => setCurrentMessage(e.target.value)}
            onKeyPress={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
            InputProps={{
              sx: { borderRadius: '24px', bgcolor: '#F1F5F9', border: 'none', px: 2, fontSize: '0.9rem' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleSendMessage()} disabled={!currentMessage.trim()}>
                    <Send sx={{ color: 'var(--navy-main)', fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Box>

      {/* Transitioning UI for Call */}
      {isCallActive && (
        <Fade in>
          <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'var(--navy-dark)', zIndex: 2000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
            <Box sx={{ position: 'absolute', top: 40, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>BASERRI-ADITU</Typography>
              <Typography variant="caption" sx={{ opacity: 0.6 }}>LLAMADA DE VOZ ACTIVA</Typography>
            </Box>
            
            <Box sx={{ position: 'relative', width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box className="pulse-ring" sx={{ position: 'absolute', width: '100%', height: '100%', borderRadius: '50%', border: '4px solid var(--emerald-main)', animation: 'pulse 2s infinite' }} />
              <Avatar sx={{ width: 120, height: 120, bgcolor: 'var(--emerald-main)', boxShadow: '0 0 40px rgba(16, 185, 129, 0.4)' }}>
                <SmartToy sx={{ fontSize: 60 }} />
              </Avatar>
            </Box>

            <Box sx={{ mt: 6, width: '80%', display: 'flex', justifyContent: 'center', gap: 1 }}>
              {[...Array(6)].map((_, i) => (
                <Box key={i} sx={{ width: 4, height: 20, bgcolor: 'var(--emerald-main)', borderRadius: 2, animation: 'wave 1s infinite', animationDelay: `${i * 0.1}s` }} />
              ))}
            </Box>

            <Box sx={{ position: 'absolute', bottom: 60 }}>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<Phone sx={{ transform: 'rotate(135deg)' }} />} 
                onClick={toggleCall}
                sx={{ borderRadius: '40px', px: 6, py: 2, fontWeight: 900, boxShadow: 'var(--shadow-lg)' }}
              >
                FINALIZAR
              </Button>
            </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default GanaderoMobile;
